import { useTranslation } from "react-i18next";
import { ExternalLink, EyeOff, Loader2 } from "lucide-react";
import { usePortalPublications } from "@/hooks/usePortalPublications";
import {
  portalDisplayStatus,
  portalErrorText,
  type PortalBadgeKind,
  type PortalPublicationRow,
} from "@/lib/portalPublish";
import { PORTALS } from "@/lib/portals";
import { formatDate } from "@/v2/lib/formatDate";
import { cn } from "@/lib/utils";

/**
 * Retour visuel du statut Immoweb (table `portal_publications`, mise à jour
 * par l'edge function `portal-status-poll` toutes les 5 min) — demande
 * Thibaud 2026-09-16 : jusqu'ici rien dans l'UI ne lisait ce statut, on
 * découvrait un REJECTED en base.
 *
 *  - `ImmowebStatusLine` : ligne sous le bouton « Publier » de la fiche
 *    (location et vente), avec la date, le serveur (sandbox) et le message
 *    d'erreur Immoweb en clair quand la publication est refusée.
 *  - `PortalStatusDot` : pastille + libellé pour une cellule de liste
 *    (Publication Vente / Publication Location).
 */

const DOT_CLASS: Record<PortalBadgeKind, string> = {
  never: "bg-text-muted/30",
  pending: "bg-warning animate-pulse",
  success: "bg-success",
  error: "bg-danger",
  deactivated: "bg-text-muted/50",
  warning: "bg-warning",
};

const LABEL_CLASS: Record<PortalBadgeKind, string> = {
  never: "text-text-muted",
  pending: "text-warning",
  success: "text-success",
  error: "text-danger",
  deactivated: "text-text-muted",
  warning: "text-warning",
};

const DATE_TIME: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

function usePortalStatusParts(
  row: PortalPublicationRow | null | undefined,
  statutPublication: string | null | undefined,
) {
  const { t, i18n } = useTranslation();
  // `status` seul dirait « Publié » sur une dépublication traitée (PROCESSED),
  // ou en vert sur une annonce fantôme (en ligne côté portail, bien Retiré).
  const { kind, statusKey } = portalDisplayStatus(row, statutPublication);
  const label = t(`portals.status.${statusKey}`, { defaultValue: statusKey });
  const when = row ? formatDate(row.updated_at, i18n.language, DATE_TIME) : "";
  const sandbox = row?.target_server === "TEST";
  const error =
    kind === "error" ? portalErrorText(row) : statusKey === "STILL_ONLINE" ? t("portals.stillOnlineHint") : "";
  return { kind, statusKey, label, when, sandbox, error };
}

export function PortalStatusDot({
  row,
  statutPublication,
}: {
  row: PortalPublicationRow | null | undefined;
  /** Statut de publication du bien chez nous — détecte l'annonce fantôme. */
  statutPublication?: string | null;
}) {
  const { t } = useTranslation();
  const { kind, label, when, sandbox, error } = usePortalStatusParts(row, statutPublication);
  const tooltip = [label, when, sandbox ? t("portals.sandboxTag") : "", error]
    .filter(Boolean)
    .join(" · ");
  return (
    <span title={tooltip} className="inline-flex items-center gap-1.5 whitespace-nowrap">
      <span className={cn("w-2.5 h-2.5 rounded-full inline-block shrink-0", DOT_CLASS[kind])} />
      <span className={cn("text-xs font-medium", LABEL_CLASS[kind])}>{label}</span>
    </span>
  );
}

export function ImmowebStatusLine({
  bienId,
  codeImmoweb,
  statutPublication,
  onDeactivate,
  deactivating = false,
}: {
  bienId: string;
  codeImmoweb?: string | null;
  /** Statut de publication du bien chez nous — détecte l'annonce fantôme. */
  statutPublication?: string | null;
  /** Action « Dépublier » proposée quand l'annonce est encore en ligne à tort. */
  onDeactivate?: () => void;
  deactivating?: boolean;
}) {
  const { t } = useTranslation();
  const { data } = usePortalPublications(bienId);
  const row = data?.immoweb ?? null;
  const { kind, statusKey, label, when, sandbox, error } = usePortalStatusParts(row, statutPublication);
  const showDeactivate = statusKey === "STILL_ONLINE" && !!onDeactivate;
  // Lien vers l'annonce uniquement en prod et une fois traitée : le sandbox
  // (test.immoweb.be) est derrière une connexion Cloudflare Access.
  const code = row?.portal_code || codeImmoweb || "";
  const publicUrl =
    row?.target_server === "PROD" && row.status === "PROCESSED" && code
      ? PORTALS.immoweb.publicUrl?.(code)
      : undefined;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs flex-wrap">
        <span className={cn("w-2.5 h-2.5 rounded-full inline-block shrink-0", DOT_CLASS[kind])} />
        <span className="font-semibold uppercase tracking-wider text-text-muted">{PORTALS.immoweb.label}</span>
        <span className={cn("font-medium", LABEL_CLASS[kind])}>{label}</span>
        {when && <span className="text-text-muted">{`· ${when}`}</span>}
        {sandbox && (
          <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-surface-2 text-text-muted">
            {t("portals.sandboxTag")}
          </span>
        )}
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-text-secondary hover:text-text-primary transition-colors"
          >
            <ExternalLink size={12} />
            {t("portals.viewListing")}
          </a>
        )}
        {showDeactivate && (
          <button
            type="button"
            onClick={onDeactivate}
            disabled={deactivating}
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md border border-warning text-warning",
              "outline-none shadow-none transition-colors hover:bg-warning/10 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer",
            )}
          >
            {deactivating ? <Loader2 size={12} className="animate-spin" /> : <EyeOff size={12} />}
            {t("portals.unpublishNow")}
          </button>
        )}
      </div>
      {error && (
        <p className={cn("text-xs leading-snug pl-4", kind === "error" ? "text-danger" : "text-warning")}>{error}</p>
      )}
    </div>
  );
}
